import React, { useEffect, useMemo, useState } from 'react';
import { BadgePercent, ChevronRight, FileSpreadsheet, Package, Search, Settings2, Tag, ArrowUpRight, Users, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RequirePermission from '../../components/AccessControl/RequirePermission';
import { useStore } from '../../context/StoreContext';
import { loadServices, errorText } from './pricelistUtils';
import styles from './Pricelist.module.css';

// Price list hub: numbers at a glance + shortcuts
const Pricelist = () => {
    const { activeStore } = useStore();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [error, setError] = useState('');
    const storeId = activeStore?.id || activeStore?._id;

    useEffect(() => {
        if (!storeId || storeId === 'global_hq') return;
        let alive = true;
        loadServices(storeId).then((d) => { if (alive) { setItems(d); setError(''); } }).catch((e) => alive && setError(errorText(e)));
        return () => { alive = false; };
    }, [storeId]);

    const audiences = useMemo(() => [...new Set(items.map((i) => i.targetDemographic).filter(Boolean))], [items]);
    const metrics = useMemo(() => ({
        services: items.length,
        addons: items.reduce((t, i) => t + (Array.isArray(i.addons) ? i.addons.length : 0), 0),
        categories: new Set(items.map((i) => `${i.targetDemographic}|${i.category}`)).size,
        offers: items.filter((i) => i.discount?.isActive).length,
    }), [items]);

    const openCatalog = () => navigate('/pricelist');
    const openMaster = () => navigate('/pricelist/master');

    const cards = [
        { id: 'services', icon: Tag, title: 'Services & Pricing', text: 'Duration, base price and the final price for every service.', value: metrics.services, label: 'Total services', action: openCatalog, actionText: 'View catalogue', tone: 'tonePrimary' },
        { id: 'audiences', icon: Users, title: 'Audience sections', text: audiences.length ? `${audiences.slice(0, 4).join(', ')}${audiences.length > 4 ? '…' : ''}. Upload all together; the list splits them for you.` : 'Men, Women, Kids… upload them together in one file.', value: audiences.length, label: 'Sections', action: openCatalog, actionText: 'Browse sections', tone: 'toneGreen' },
        { id: 'addons', icon: Package, title: 'Add-ons & Upgrades', text: 'Extras on a service to raise the average bill.', value: metrics.addons, label: 'Add-ons', action: () => navigate('/pricelist/add'), actionText: 'Manage add-ons', tone: 'toneBlue' },
        { id: 'offers', icon: BadgePercent, title: 'Offers & Discounts', text: '% or flat offers on selected services.', value: metrics.offers, label: 'Active offers', action: openMaster, actionText: 'Master editor', tone: 'toneAmber' },
    ];

    return (
        <div className={styles.pageWrapper}>
            <header className={styles.header}>
                <div className={styles.headerIdentity}>
                    <div className={styles.iconBox}><Tag size={16} strokeWidth={2.5} /></div>
                    <div className={styles.headerText}>
                        <h1>Price list</h1>
                        <p>Services, sections, add-ons and offers for this branch.</p>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.outlineBtn} onClick={openCatalog}><Search size={14} /> View catalogue</button>
                    <RequirePermission module="pricelist" action="edit">
                        <button type="button" className={styles.primaryBtn} onClick={openMaster}><Settings2 size={14} /> Master price list</button>
                    </RequirePermission>
                </div>
            </header>

            <main className={styles.workspace}>
                <div className={styles.workspaceInner}>
                    {error && <div className={styles.sectionHeader} style={{ color: 'var(--h-color-danger-600, #be1025)', display: 'flex', gap: 6, alignItems: 'center' }}><AlertCircle size={14} /> {error}</div>}
                    <section className={styles.heroBanner}>
                        <div className={styles.heroContent}>
                            <span className={styles.eyebrow}>CATALOGUE CONTROL CENTRE</span>
                            <h2>Your whole service menu in one place.</h2>
                            <p>Upload Men, Women and Kids services together from one Excel file. Each goes into its own section; same name in two sections stays two services.</p>
                        </div>
                        <RequirePermission module="pricelist" action="edit">
                            <button type="button" className={styles.heroBtn} onClick={openMaster}>Open master editor <ArrowUpRight size={16} /></button>
                        </RequirePermission>
                    </section>

                    <section className={styles.bentoGrid}>
                        {cards.map(({ id, icon: Icon, title, text, value, label, action, actionText, tone }) => (
                            <div className={styles.bentoCard} key={id}>
                                <div className={styles.cardTop}>
                                    <div className={`${styles.cardIconWrap} ${styles[tone]}`}><Icon size={16} strokeWidth={2.5} /></div>
                                    <div className={styles.metricBlock}><span className={styles.metricValue}>{value}</span><span className={styles.metricLabel}>{label}</span></div>
                                </div>
                                <div className={styles.cardMiddle}><h3>{title}</h3><p>{text}</p></div>
                                <div className={styles.cardBottom}><button type="button" className={styles.ghostBtn} onClick={action}>{actionText} <ChevronRight size={14} /></button></div>
                            </div>
                        ))}
                    </section>

                    <section className={styles.quickActionsSection}>
                        <div className={styles.sectionHeader}><h3>Quick operations</h3><p>Jump straight to the work you do most.</p></div>
                        <div className={styles.quickActionsGrid}>
                            <button type="button" className={styles.actionRowCard} onClick={openCatalog}>
                                <div className={styles.actionIcon}><Tag size={16} /></div>
                                <div className={styles.actionText}><strong>Service price list</strong><span>Search, edit and remove services by section</span></div>
                                <ChevronRight size={16} className={styles.actionArrow} />
                            </button>
                            <RequirePermission module="pricelist" action="edit">
                                <button type="button" className={styles.actionRowCard} onClick={openMaster}>
                                    <div className={styles.actionIcon}><FileSpreadsheet size={16} /></div>
                                    <div className={styles.actionText}><strong>Master editor and Excel upload</strong><span>All sections, services and add-ons together</span></div>
                                    <ChevronRight size={16} className={styles.actionArrow} />
                                </button>
                            </RequirePermission>
                            <RequirePermission module="pricelist" action="manage_addons">
                                <button type="button" className={styles.actionRowCard} onClick={() => navigate('/pricelist/add')}>
                                    <div className={styles.actionIcon}><Package size={16} /></div>
                                    <div className={styles.actionText}><strong>Add-on manager</strong><span>Extras for any service</span></div>
                                    <ChevronRight size={16} className={styles.actionArrow} />
                                </button>
                            </RequirePermission>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default Pricelist;
